import * as React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
} from "react-admin";
import type { AgentDispatchRecord } from "../../models/AgentDispatchRecord";
import type { Cargo } from "../../models/Cargo";
import {
  getAgentDispatchStatusChoices,
  getAgentDispatchStatusColor,
  getAgentDispatchStatusTitle,
  isAgentDispatchStatus,
} from "../../constants/AgentDispatchStatus";
import { formatDate } from "../../utils/common";
import { AgentCargoInfoView, parseCargoRecord } from "./agent_cargo_info";
import { AgentRecordDetailDialog } from "./agent_record_detail";

const STATUS_ALL = "all";

const statusFilterChoices = [
  { value: STATUS_ALL, label: "全部状态" },
  ...getAgentDispatchStatusChoices().map((item) => ({
    value: item.id,
    label: item.name,
  })),
];

/**
 * 从列表行中解析记录 ID。
 */
const getRecordId = (record: AgentDispatchRecord | Record<string, unknown> | undefined): string => {
  if (!record) {
    return "";
  }
  const value = (record as AgentDispatchRecord).id
    ?? (record as Record<string, unknown>)._id
    ?? (record as Record<string, unknown>).recordId
    ?? (record as Record<string, unknown>).record_id;
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "";
};

/**
 * 格式化时间展示。
 */
const formatDateValue = (value: unknown): string => {
  if (typeof value !== "string" || !value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 解析当前筛选中的状态值。
 */
const parseStatusFilterValue = (value: unknown): string => {
  if (typeof value === "string" && (value === STATUS_ALL || isAgentDispatchStatus(value))) {
    return value;
  }
  return STATUS_ALL;
};

/**
 * 列表顶部状态筛选栏。
 */
const ListActions: React.FC = () => {
  const { filterValues, setFilters } = useListContext();
  const [draftStatus, setDraftStatus] = React.useState<string>(
    parseStatusFilterValue(filterValues.status)
  );

  React.useEffect(() => {
    setDraftStatus(parseStatusFilterValue(filterValues.status));
  }, [filterValues.status]);

  const handleSearch = () => {
    const nextFilters = {
      ...filterValues,
      status: draftStatus !== STATUS_ALL ? draftStatus : undefined,
    };
    setFilters(nextFilters, undefined);
  };

  const handleReset = () => {
    setDraftStatus(STATUS_ALL);
    const nextFilters = { ...filterValues } as Record<string, unknown>;
    delete nextFilters.status;
    setFilters(nextFilters, undefined);
  };

  return (
    <TopToolbar sx={{ width: "100%", alignItems: "center", gap: 1, flexWrap: "wrap", px: 2, py: 1 }}>
      <MuiTextField
        select
        size="small"
        label="推送状态"
        value={draftStatus}
        onChange={(event) => setDraftStatus(event.target.value)}
        sx={{ minWidth: 200 }}
      >
        {statusFilterChoices.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </MuiTextField>
      <Button variant="contained" onClick={handleSearch}>
        查询
      </Button>
      <Button onClick={handleReset}>重置</Button>
    </TopToolbar>
  );
};

/**
 * 按货源 ID 批量请求货源信息。
 */
const useCargosByIds = (cargoIds: string[]) => {
  const dataProvider = useDataProvider();
  const [cargosById, setCargosById] = React.useState<Record<string, Cargo>>({});
  const [loading, setLoading] = React.useState(false);
  const requestedIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const uniqueIds = Array.from(new Set(cargoIds.filter(Boolean)));
    const missingIds = uniqueIds.filter(
      (id) => !cargosById[id] && !requestedIdsRef.current.has(id)
    );
    if (missingIds.length === 0) {
      return;
    }

    missingIds.forEach((id) => requestedIdsRef.current.add(id));
    let cancelled = false;

    const fetchCargos = async () => {
      try {
        setLoading(true);
        const next: Record<string, Cargo> = {};

        try {
          const response = await dataProvider.getMany("validCargos", { ids: missingIds });
          const records = Array.isArray(response?.data) ? response.data : [];
          for (const raw of records) {
            const cargo = parseCargoRecord(raw);
            if (!cargo?.id) {
              continue;
            }
            next[cargo.id] = cargo;
          }
        } catch {
          // getMany 不可用时回退到逐条 getOne
        }

        const stillMissing = missingIds.filter((id) => !next[id]);
        if (stillMissing.length > 0) {
          const settled = await Promise.allSettled(
            stillMissing.map((id) => dataProvider.getOne("validCargos", { id }))
          );
          for (const result of settled) {
            if (result.status !== "fulfilled") {
              continue;
            }
            const cargo = parseCargoRecord(result.value.data);
            if (!cargo?.id) {
              continue;
            }
            next[cargo.id] = cargo;
          }
        }

        if (!cancelled && Object.keys(next).length > 0) {
          setCargosById((previous) => ({ ...previous, ...next }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchCargos();

    return () => {
      cancelled = true;
    };
  }, [cargoIds, cargosById, dataProvider]);

  return { cargosById, loading };
};

/**
 * 智能体操作记录表格（含详情弹窗）。
 */
const AgentRecordsDatagrid: React.FC = () => {
  const notify = useNotify();
  const { data } = useListContext<AgentDispatchRecord>();
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedRecordId, setSelectedRecordId] = React.useState("");
  const [selectedRecord, setSelectedRecord] = React.useState<AgentDispatchRecord | null>(null);

  const cargoIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const record of Object.values(data ?? {})) {
      if (record?.cargoId) {
        ids.add(record.cargoId);
      }
    }
    return Array.from(ids);
  }, [data]);

  const { cargosById, loading: cargosLoading } = useCargosByIds(cargoIds);

  const handleOpenDetail = (record: AgentDispatchRecord) => {
    const recordId = getRecordId(record);
    if (!recordId) {
      notify("该记录缺少 ID，无法查看详情", { type: "warning" });
      return;
    }
    setSelectedRecordId(recordId);
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRecord(null);
    setSelectedRecordId("");
  };

  return (
    <>
      {cargosLoading && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            正在加载本页货源信息...
          </Typography>
        </Box>
      )}
      <Datagrid
        rowClick={(_, __, record) => {
          handleOpenDetail(record as AgentDispatchRecord);
          return false as const;
        }}
        bulkActionButtons={false}
      >
        <TextField source="id" label="记录 ID" />
        <FunctionField
          label="操作类型"
          sortBy="type"
          render={(record: AgentDispatchRecord) => {
            const label = record?.type || record?.title;
            if (!label) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return <Chip label={label} size="small" variant="outlined" />;
          }}
        />
        <FunctionField
          label="状态"
          sortBy="status"
          render={(record: AgentDispatchRecord) => {
            if (!record?.status) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <Chip
                label={getAgentDispatchStatusTitle(record.status)}
                size="small"
                color={getAgentDispatchStatusColor(record.status)}
              />
            );
          }}
        />
        <FunctionField
          label="货源信息"
          render={(record: AgentDispatchRecord) => {
            if (!record?.cargoId) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            const cargo = cargosById[record.cargoId] ?? null;
            return (
              <AgentCargoInfoView
                cargo={cargo}
                loading={cargosLoading && !cargo}
                compact
                emptyText={`货源 ${record.cargoId}`}
              />
            );
          }}
        />
        <FunctionField
          label="摘要"
          render={(record: AgentDispatchRecord) => {
            const text = record?.summary || record?.content || record?.title || "-";
            return (
              <Typography variant="body2" noWrap title={text} sx={{ maxWidth: 280 }}>
                {text}
              </Typography>
            );
          }}
        />
        <FunctionField
          label="关联信息"
          render={(record: AgentDispatchRecord) => {
            if (!record) {
              return "-";
            }
            const hasRelated = Boolean(record.driverId || record.waybillId);
            if (!hasRelated) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <Stack spacing={0.25} sx={{ maxWidth: 220 }}>
                {record.driverId && (
                  <Typography variant="caption" color="text.secondary">
                    司机：{record.driverId}
                  </Typography>
                )}
                {record.waybillId && (
                  <Typography variant="caption" color="text.secondary">
                    运单：{record.waybillId}
                  </Typography>
                )}
              </Stack>
            );
          }}
        />
        <FunctionField
          label="创建时间"
          source="createdAt"
          sortBy="createdAt"
          sortByOrder="DESC"
          render={(record: AgentDispatchRecord) => formatDateValue(record?.createdAt)}
        />
        <FunctionField
          label="操作"
          render={(record: AgentDispatchRecord) => {
            const recordId = getRecordId(record);
            return (
              <Box onClick={(event) => event.stopPropagation()}>
                <Tooltip title="查看详情">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!recordId}
                      onClick={() => handleOpenDetail(record)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          }}
        />
      </Datagrid>
      <AgentRecordDetailDialog
        open={detailOpen}
        recordId={selectedRecordId}
        initialRecord={selectedRecord}
        onClose={handleCloseDetail}
      />
    </>
  );
};

/**
 * 智能体操作记录列表页（走标准 getList 分页）。
 */
const AgentRecordsPage: React.FC = (props) => {
  return (
    <List
      {...props}
      title="智能体操作记录"
      actions={<ListActions />}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={25}
    >
      <AgentRecordsDatagrid />
    </List>
  );
};

export default AgentRecordsPage;
